import { type ReactNode, useEffect } from 'react'
import { X } from 'lucide-react'
import styles from './Drawer.module.css'

interface Props {
  aperto: boolean
  titolo?: ReactNode
  onChiudi: () => void
  larghezza?: number
  /** Se passato, viene reso in fondo al drawer come footer sticky */
  footer?: ReactNode
  children: ReactNode
}

/**
 * Pannello laterale (slide da destra) per form ricchi.
 * Versione "drawer" del Modal: layout adatto a form con molti campi.
 *
 * - Esc o click sull'overlay chiudono il drawer
 * - Body scroll bloccato mentre aperto
 * - Su mobile (<640px) occupa tutta la larghezza dello schermo
 */
export function Drawer({ aperto, titolo, onChiudi, larghezza = 560, footer, children }: Props) {
  useEffect(() => {
    if (!aperto) return
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onChiudi() }
    document.addEventListener('keydown', onEsc)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.body.style.overflow = ''
    }
  }, [aperto, onChiudi])

  if (!aperto) return null
  return (
    <div className={styles.overlay} onClick={onChiudi}>
      <aside
        className={styles.drawer}
        style={{ maxWidth: larghezza }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.header}>
          <h2 className={styles.titolo}>{titolo}</h2>
          <button className={styles.close} onClick={onChiudi} aria-label="Chiudi">
            <X size={18} />
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </aside>
    </div>
  )
}
